import React, { useEffect, useState, useCallback } from "react"
import axios from 'axios'
import { Card, Col, Row, Breadcrumb, Collapse, message, Space, Tabs, Table, Typography, Spin, Select, Input, notification } from "antd"
import { CaretRightOutlined } from '@ant-design/icons'
import { startCase } from "lodash"

import { COD_SERVICE_PROVIDERS } from "../../constants/serviceProviders"
import DragUpload from "../Form/DragUpload"
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { Text } = Typography;

const UNITS = ["RM", "PCT"]

const auditInfoLabel = {
  updatedBy: "Last Updated By",
  updatedAt: "Last Updated At",
  reason: "Reason"
}

const columnTypes = [
  { 
    title: "Type", 
    dataIndex: "type", 
    render: text => {
      let header = startCase(text).toUpperCase()
      UNITS.forEach(unit => {
        if (header.includes(unit)) {
          header = header.replace(unit, `(${unit})`)
        }
      })

      return header
    },
  },
  ...(COD_SERVICE_PROVIDERS.map(serviceProvider => ({ 
    title: serviceProvider, 
    dataIndex: serviceProvider, 
    render: text => text ? Number(text).toFixed(2) : '-'
  })))
];

const CourierPackagePricing = () => {
  const { permissions } = usePermissions();
  const routes = [
    { path: "", breadcrumbName: "App" },
    { path: "/courier-package/pricing", breadcrumbName: "Domestic Courier Pricing" },
  ];

  const [isLoading, setIsLoading] = useState(false)
  const [availablePackages, setAvailablePackages] = useState([])
  const [packagePricings, setPackagePricings] = useState({})
  const [activeTab, setActiveTab] = useState(null)
  const [typeSearch, setTypeSearch] = useState("")
  const [reason, setReason] = useState("")
  const [isAuditLoading, setIsAuditLoading] = useState(false)
  const [auditInfo, setAuditInfo] = useState(null)

  const filterRows = (rows) => {
    if (!rows) return rows

    const search = typeSearch.trim().toLowerCase()
    if (!search) return rows

    return rows.filter((row) => String(row.type || "").toLowerCase().includes(search))
  }

  const fetchPackagePricings = useCallback(async () => {
    if (!activeTab || packagePricings[activeTab]) { 
      return 
    }

    setIsLoading(true)
    const res = await axios.get(`/api/courier-packages/pricings`, { params: { package: activeTab } })

    setPackagePricings(prevPackagePricings => ({
      ...prevPackagePricings,
      [activeTab]: res.data.data
    }))

    setIsLoading(false)
  }, [activeTab])

  const fetchLatestPricingAudit = useCallback(async () => {
    setIsAuditLoading(true)
    try {
      const res = await axios.get("/api/admin-audit-logs", {
        params: {
          subjectType: "CourierPricing",
          auditType: "ADMIN",
          limit: 1,
        },
      })
      const auditLogs = res.data.data || []
      if (auditLogs.length > 0) {
        const latestAudit = auditLogs[0]
        setAuditInfo({
          updatedBy: latestAudit.updatedBy?.email || latestAudit.updatedBy?.username || "Unknown",
          updatedAt: new Date(latestAudit.createdAt).toLocaleString(),
          reason: latestAudit.changes?.reason || "No reason provided",
        })
      } else {
        setAuditInfo({
          updatedBy: "N/A",
          updatedAt: "N/A",
          reason: "No pricing updates recorded yet",
        })
      }
    } catch (error) {
      message.error("Failed to fetch pricing audit history")
      console.error(error)
    } finally {
      setIsAuditLoading(false)
    }
  }, [])

  const handleUploadSuccess = () => {
    setReason("")
    notification.open({
      message: 'Processing',
      description: "Please check the progress in System -> Jobs",
      duration: 0,
      style: { width: 500 }
    });
  }

  useEffect(() => {
    const getAvailablePackages = async () => {
      const response = await axios.get(`/api/courier-packages`)
      const packages = response.data.meta?.packages || []

      setAvailablePackages(packages)
      setActiveTab("normal")
    }

    getAvailablePackages()
  }, [])

  useEffect(() => { fetchPackagePricings() }, [activeTab, fetchPackagePricings])

  useEffect(() => {
    fetchLatestPricingAudit()
  }, [fetchLatestPricingAudit])

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">Domestic Courier Pricing</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "16px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      {permissions?.includes(PERMISSIONS.UPLOAD_PRICING) && (
        <div className="upload">
          <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
            <Collapse.Panel header="Upload New Pricing" key="upload">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text strong>Reason for pricing update: </Text>
                  <Row>
                    <Input.TextArea
                      label="Reason for Pricing Update"
                      name="reason"
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      placeholder="Enter reason for pricing update"
                      style={{ marginBottom: 16, width: "100%" }}
                      rows={2}
                    />
                  </Row>
                </Space>
                <DragUpload
                  action={`${process.env.REACT_APP_SERVER_URL}/api/courier-packages/pricing`}
                  accept=".xls, .xlsx"
                  method="put"
                  data={{ reason }}
                  beforeUpload={() => {
                    if (!reason.trim()) {
                      message.error("Please enter a reason for the pricing update.");
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  }}
                  onUploadSuccess={handleUploadSuccess}
                />
              </Space>
            </Collapse.Panel>
          </Collapse>
        </div>)}

        {permissions.includes(PERMISSIONS.VIEW_ADMIN_AUDIT_LOGS)
          && !isAuditLoading
          && auditInfo
          && (
          <Card>
            {Object.entries(auditInfoLabel).map(([key, label]) => (
              <Row key={key}>
                <span>
                  <Text strong>{label}:{` `} </Text> {auditInfo[key]}
                </span>
              </Row>
            ))}
          </Card>
        )}

        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Filters" key="filters">
            <Row gutter={12} align="middle">
              <Col>
                <Select
                  showSearch
                  placeholder="Jump to package"
                  style={{ width: 200 }}
                  value={activeTab}
                  onChange={setActiveTab}
                  optionFilterProp="children"
                >
                  {availablePackages.map((type) => (
                    <Select.Option key={type} value={type}>{startCase(type)}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Input.Search
                  placeholder="Filter by type"
                  allowClear
                  onSearch={setTypeSearch}
                  style={{ width: 220 }}
                />
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <div className="table">
          <Tabs activeKey={activeTab} animated={true} onTabClick={tab => setActiveTab(tab)}>
            { availablePackages.length && availablePackages.map(type => (
              <Tabs.TabPane tab={startCase(type)} key={type}>
                <Table
                  rowKey="Id"
                  className="table-section"
                  columns={columnTypes}
                  dataSource={filterRows(packagePricings[type])}
                  loading={isLoading}
                  pagination={{ pageSize: 50 }}
                  locale={{ emptyText: "No pricing data uploaded yet for this package." }}
                />
              </Tabs.TabPane>
            )) }
          </Tabs>

          { !availablePackages.length && <Spin size="large" /> }
        </div>
      </Space>
    </div>
  )
}

export default CourierPackagePricing;